import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface CheckboxGroupOption {
  value: string;
  label: string;
  description?: React.ReactNode;
  disabled?: boolean;
}

interface FormCheckboxGroupFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  options: CheckboxGroupOption[] | string[];
  description?: React.ReactNode;
  required?: boolean;
  className?: string;
  /** Stack options vertically (`col`, default) or wrap horizontally (`wrap`). */
  layout?: 'col' | 'wrap';
}

function normalizeOptions(options: CheckboxGroupOption[] | string[]): CheckboxGroupOption[] {
  if (options.length === 0) return [];
  if (typeof options[0] === 'string') {
    return (options as string[]).map((v) => ({ value: v, label: v }));
  }
  return options as CheckboxGroupOption[];
}

/**
 * Multi-select rendered as a vertical (or wrapping) list of checkboxes, bound
 * to a `string[]` form value. Use when the option count is small enough that
 * all choices should be visible at once.
 */
export const FormCheckboxGroupField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  options,
  description,
  required,
  className,
  layout = 'col',
}: FormCheckboxGroupFieldProps<TFieldValues>) => {
  const normalized = normalizeOptions(options);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const values = (field.value as string[] | undefined) ?? [];
        const toggle = (opt: string, checked: boolean) => {
          if (checked) field.onChange([...values, opt]);
          else field.onChange(values.filter((v) => v !== opt));
        };

        return (
          <FormItem className={className}>
            <FormLabel>
              {label} {required && <span className="text-primary">*</span>}
            </FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
            <FormControl>
              <div
                className={cn(layout === 'col' ? 'grid gap-2' : 'flex flex-wrap gap-x-4 gap-y-2')}
              >
                {normalized.map((opt) => {
                  const checked = values.includes(opt.value);
                  const id = `${name}-${opt.value}`;
                  return (
                    <div key={opt.value} className="flex items-start gap-2">
                      <Checkbox
                        id={id}
                        checked={checked}
                        disabled={opt.disabled}
                        onCheckedChange={(v: boolean | 'indeterminate') =>
                          toggle(opt.value, v === true)
                        }
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <Label htmlFor={id} className="cursor-pointer font-normal">
                          {opt.label}
                        </Label>
                        {opt.description && (
                          <p className="text-xs text-muted-foreground">{opt.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
FormCheckboxGroupField.displayName = 'FormCheckboxGroupField';
